import { useFragment } from '@apollo/client';
import type { EventInfo } from '@ps/isp-web-services-types';
import React from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import type { LiveData } from '@swf/sports/utils/getLiveData';
import {
  getIsAmericanFootballLiveData,
  getIsBaseballLiveData,
  getIsBasketballLiveData,
  getIsDartsLiveData,
  getIsESportsLiveData,
  getIsFootballLiveData,
  getIsIceHockeyLiveData,
  getIsTableTennisLiveData,
  getIsTennisLiveData
} from '@swf/sports/utils/getLiveData';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import type { EventStatus } from '../EventStatus/hooks/useEventStatus';

import AmericanFootball from './components/AmericanFootball/AmericanFootball';
import BackgroundImage from './components/BackgroundImage/BackgroundImage';
import Baseball from './components/Baseball/Baseball';
import Basketball from './components/Basketball/Basketball';
import Darts from './components/Darts/Darts';
import DefaultEventCard from './components/DefaultEventCard/DefaultEventCard';
import ESports from './components/ESports/ESports';
import Football from './components/Football/Football';
import IceHockey from './components/IceHockey/IceHockey';
import TableTennis from './components/TableTennis/TableTennis';
import Tennis from './components/Tennis/Tennis';

export interface Props extends EventInfo {
  eventStatusOverride?: EventStatus;
  children?: React.ReactNode;
}

const EventCard: React.FC<Props> = ({
  eventTypeId,
  eventId,
  name,
  eventTime,
  inPlay,
  eventStatusOverride,
  offeredInPlay,
  logoUrl,
  enrichedParticipants,
  children: toggleComponent
}) => {
  const { variables } = useLiveData();
  const { data } = useFragment<LiveData>(
    getEventScoreboardFragment(eventTypeId, eventId, variables)
  );
  const isEventTypeLiveDataEnabled = useIsEventTypeLiveDataEnabled(eventTypeId);
  let scoreboard = null;

  if (isEventTypeLiveDataEnabled && inPlay) {
    if (getIsAmericanFootballLiveData(eventTypeId, data)) {
      scoreboard = (
        <AmericanFootball
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          americanFootballFixture={data}
        />
      );
    } else if (getIsBaseballLiveData(eventTypeId, data)) {
      scoreboard = (
        <Baseball
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          baseballFixture={data}
        />
      );
    } else if (getIsBasketballLiveData(eventTypeId, data)) {
      scoreboard = (
        <Basketball
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          basketballFixture={data}
        />
      );
    } else if (getIsDartsLiveData(eventTypeId, data)) {
      scoreboard = (
        <Darts
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          dartsFixture={data}
        />
      );
    } else if (getIsESportsLiveData(eventTypeId, data)) {
      scoreboard = (
        <ESports
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          eSportsFixture={data}
        />
      );
    } else if (getIsFootballLiveData(eventTypeId, data)) {
      scoreboard = (
        <Football
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          footballFixture={data}
        />
      );
    } else if (getIsIceHockeyLiveData(eventTypeId, data)) {
      scoreboard = (
        <IceHockey
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          iceHockeyFixture={data}
        />
      );
    } else if (getIsTableTennisLiveData(eventTypeId, data)) {
      scoreboard = (
        <TableTennis
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          tableTennisFixture={data}
        />
      );
    } else if (getIsTennisLiveData(eventTypeId, data)) {
      scoreboard = (
        <Tennis
          eventId={eventId}
          eventTime={eventTime}
          enrichedParticipants={enrichedParticipants}
          tennisMatch={data}
        />
      );
    }
  }

  return (
    <>
      <DefaultEventCard
        eventTypeId={eventTypeId}
        title={name}
        isTurnInPlay={offeredInPlay}
        isInPlay={inPlay}
        eventTime={eventTime}
        eventStatusOverride={eventStatusOverride}
        isTitleOnly={Boolean(scoreboard)}
        logoUrl={logoUrl}
      />
      {scoreboard || toggleComponent ? (
        <BackgroundImage eventTypeId={eventTypeId}>
          {scoreboard}
          {toggleComponent}
        </BackgroundImage>
      ) : null}
    </>
  );
};

export { EventCard };
